import { useEffect, useState } from 'react'
import { Typography, ToggleButton, ToggleButtonGroup } from '@mui/material'

import { ImpactsLibraryPagination } from 'src/components/impacts-library/impacts-library-list/impacts-library-pagination.component'
import { StockImagesFilter } from './stock-images-filter.component'
import { ItemsList } from './items-list.component'
import { VideosList } from './videos-list-component'
import {
  initialStockImagesFilterValues,
  useDebounce,
  usePagination,
  useStockImages,
} from 'src/hooks'

import { countTotalPages } from 'src/common/utils/count-pages.util'
import { useStyles } from './styles'
import { ISetStockImage, ISetStockVideo } from 'src/types'
import { IFilter } from 'src/components/filter/types/filter.types'
import { HelperWrapper } from 'src/components/helper-wrapper'

const defaultPage = 0
const defaultLimit = 20

interface IProps {
  setImage: ISetStockImage
  setVideo?: ISetStockVideo
  withVideo?: boolean
}

const StockImagesModalInner = ({ setImage, setVideo, withVideo }: IProps) => {
  const [filter, setFilter] = useState<IFilter>(initialStockImagesFilterValues)
  const [switchValue, setSwitchValue] = useState(false)
  const debouncedQuery = useDebounce(filter.query, 500)
  const { page, setPage } = usePagination(defaultPage, defaultLimit)

  const { data, isLoading } = useStockImages(
    {
      query: debouncedQuery,
      page: page + 1,
      limit: defaultLimit,
    },
    switchValue,
  )

  const classes = useStyles()

  useEffect(() => {
    setPage(defaultPage)
  }, [filter, setPage])

  const handleChange = (event: React.MouseEvent<HTMLElement>, newAlignment: boolean) => {
    if (newAlignment === false) {
      setSwitchValue(false)
      return
    }
    if (newAlignment === true) {
      setSwitchValue(true)
      return
    }
    setSwitchValue(false)
  }

  const totalPages = countTotalPages(data?.total_results ?? 0, defaultLimit)

  return (
    <>
      <div className={classes.upperWrapper}>
        <Typography className={classes.modalTitle}>
          <HelperWrapper t="stock-images-title" />
        </Typography>
        <div>
          <div className={classes.actionsWrapper}>
            <StockImagesFilter setFilter={setFilter} filter={filter} />
            {withVideo && (
              <ToggleButtonGroup
                color="primary"
                exclusive
                value={switchValue}
                onChange={handleChange}
                aria-label="Platform"
              >
                <ToggleButton value={false}>
                  <HelperWrapper t="stock-images-photos" verticalPosition={40} isColorInherited />
                </ToggleButton>
                <ToggleButton value={true}>
                  <HelperWrapper t="stock-images-videos" verticalPosition={40} isColorInherited />
                </ToggleButton>
              </ToggleButtonGroup>
            )}
          </div>
          {data && !isLoading ? (
            <>
              {'videos' in data && switchValue ? (
                <VideosList setVideo={setVideo} items={data.videos} />
              ) : 'photos' in data && !switchValue ? (
                <ItemsList setImage={setImage} items={data.photos} />
              ) : null}
            </>
          ) : isLoading ? null : (
            <Typography align="center" pt={2}>
              {switchValue ? (
                <HelperWrapper t="stock-videos-load-msg" />
              ) : (
                <HelperWrapper t="stock-images-load-msg" />
              )}
            </Typography>
          )}
        </div>
      </div>
      <ImpactsLibraryPagination page={page} setPage={setPage} totalPages={totalPages} />
    </>
  )
}

export { StockImagesModalInner }
